import { useFetch } from '../../../hooks/useFetch';
import { BankHourRequest } from '../../../models/bank-hours-request';
import { errorNotification } from '../../../providers/mantine-notifications';
import { translateServerHttpErrors } from '../../../utils/helpers';
import {
  UseApprovalBankHourRequestProps,
  UseCancelBankHourRequestProps,
  UseCreateBankHourRequestPartialProps,
  UseCreateBankHourRequestProps,
  UseGetBankHoursRequestPaginatedProps,
  UseGetBankHoursRequestsProps,
} from '../../../utils/types/data/hooks/bank-hour-request';
import { PaginatedResponse } from '../../../utils/types/data/pagination';
import {
  bankRequestApprovalRequest,
  cancelBankHourRequest,
  createRequest,
  createRequestPartial,
  getBankHourRequestPaginatedRequest,
  listBankHourApprovalsRequest,
  listBankHourRequests,
} from '../../bank-hours-request';

export function useCreateBankHourRequest() {
  const {
    fetchData,
    state: { data: response, error, loading },
  } = useFetch<void>();

  const fetch = ({ onSuccess, data }: UseCreateBankHourRequestProps) =>
    fetchData({
      fetcher: () => createRequest(data),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao criar solicitação banco de horas.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return {
    fetch,
    response,
    error,
    loading,
  };
}

export function useGetBankHoursRequestPaginated() {
  const {
    fetchData,
    state: { data: response, error, loading },
  } = useFetch<PaginatedResponse<BankHourRequest>>();

  const fetch = ({ onSuccess, params }: UseGetBankHoursRequestPaginatedProps) =>
    fetchData({
      fetcher: () => getBankHourRequestPaginatedRequest(params),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao buscar solicitações banco de horas.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return {
    fetch,
    response,
    error,
    loading,
  };
}

export function useGetBankHoursRequest() {
  const {
    fetchData,
    state: { data: response, error, loading },
  } = useFetch<BankHourRequest[]>();

  const fetch = ({ onSuccess }: UseGetBankHoursRequestsProps) =>
    fetchData({
      fetcher: () => listBankHourRequests(),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao buscar solicitações banco de horas.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return {
    fetch,
    response,
    error,
    loading,
  };
}

export function useGetBankHoursApprovals() {
  const {
    fetchData,
    state: { data: response, error, loading },
  } = useFetch<BankHourRequest[]>();

  const fetch = ({ onSuccess, params }: UseGetBankHoursRequestsProps) =>
    fetchData({
      fetcher: () => listBankHourApprovalsRequest(params),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao buscar solicitações banco de horas.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return {
    fetch,
    response,
    error,
    loading,
  };
}

export function useApprovalBankHourRequest() {
  const {
    fetchData,
    state: { data: response, error, loading },
  } = useFetch<BankHourRequest>();

  const fetch = ({
    onSuccess,
    data,
    requestId,
  }: UseApprovalBankHourRequestProps) =>
    fetchData({
      fetcher: () => bankRequestApprovalRequest(requestId, data),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao avaliar solicitação banco de horas.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return {
    fetch,
    response,
    error,
    loading,
  };
}

export function useCreateBankHourRequestPartial() {
  const {
    fetchData,
    state: { data: response, error, loading },
  } = useFetch<void>();

  const fetch = ({ onSuccess, data }: UseCreateBankHourRequestPartialProps) =>
    fetchData({
      fetcher: () => createRequestPartial(data),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao criar solicitação banco de horas.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return {
    fetch,
    response,
    error,
    loading,
  };
}

export function useCancelBankHourRequest() {
  const {
    fetchData,
    state: { loading },
  } = useFetch<BankHourRequest>();

  const fetch = ({ onSuccess, requestId }: UseCancelBankHourRequestProps) =>
    fetchData({
      fetcher: () => cancelBankHourRequest(requestId),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao cancelar solicitação.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return { fetch, loading };
}
